import { api, RouterInput, RouterOutput } from "../../../services/rpc/RpcProvider";
import { useCallback } from "react";
import { useToast } from "@introist/react-foundation/v2";

export type WebhookConfig = RouterOutput["datasources"]["webhooks"]["find"];
export type WebhookConfigUpdate = RouterInput["datasources"]["webhooks"]["update"]["update"];

export const useWebhookConfig = (datasourceId: string) => {
  const { data: config } = api.datasources.webhooks.find.useQuery({ datasourceId });

  return { config, actions: useWebhookConfigActions() };
};

export const useWebhookConfigActions = () => {
  const toast = useToast();

  const createOp = api.datasources.webhooks.create.useMutation();
  const updateOp = api.datasources.webhooks.update.useMutation();
  const removeOp = api.datasources.webhooks.remove.useMutation();

  const apiUtils = api.useContext();

  const findOp = apiUtils.datasources.webhooks.find;

  const create = useCallback(
    async (datasourceId: string) => {
      return createOp
        .mutateAsync({ datasourceId })
        .then(async () => {
          await findOp.refetch();
        })
        .catch(err => {
          toast.error("Failed to enable webhook");
          throw err;
        });
    },
    [findOp, createOp, toast]
  );

  const update = useCallback(
    async (id: string, update: WebhookConfigUpdate) => {
      return updateOp
        .mutateAsync({ id, update })
        .then(async () => {
          await findOp.refetch();
          toast.success("Webhook config updated");
        })
        .catch((err: any) => {
          toast.error("Failed to update");
          throw err;
        });
    },
    [findOp, updateOp, toast]
  );

  const remove = useCallback(
    async (id: string) => {
      return removeOp
        .mutateAsync({ id })
        .then(async () => {
          await findOp.refetch();
        })
        .catch((err: any) => {
          toast.error("Failed to remove webhook");
          throw err;
        });
    },
    [findOp, removeOp, toast]
  );

  return { create, update, remove };
};
